import type { HandoffOutcome, HandoffSummary } from '../types/order-approval.type';

/**
 * Which side of a handed-off order the current organization is on.
 *
 * - `none`        -- an ordinary own-facility order (or no organization selected).
 * - `origin`      -- we delivered it into another organization's linked facility: we see
 *                    our collection data and the outcome, never their price or invoice.
 * - `destination` -- it was delivered into our facility by another organization: we
 *                    see the load and who delivered it, never where it was collected.
 */
export type HandoffRole = 'none' | 'origin' | 'destination';

export function handoffRoleFor(handoff: HandoffSummary | null | undefined, organizationId: string | undefined): HandoffRole {
  if (!handoff || !organizationId) return 'none';
  if (handoff.destination_org_id === organizationId) return 'destination';
  if (handoff.from_org_id === organizationId) return 'origin';
  return 'none';
}

export type OutcomeColor = 'success' | 'warning' | 'error' | 'default';

export function outcomeColor(outcome: HandoffOutcome | null | undefined): OutcomeColor {
  switch (outcome) {
    case 'accepted':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

/** The approval status as the outcome chips read it (approved reads as "accepted"). */
export function outcomeFromApproval(status: 'pending' | 'approved' | 'rejected' | null | undefined): HandoffOutcome | null {
  if (status === 'approved') return 'accepted';
  if (status === 'pending' || status === 'rejected') return status;
  return null;
}
